import { useContext } from "react";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import { ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import {
    ChangeInValueCellRenderer,
    UpdateBidOrBudgetCellRenderer,
} from "@cell-renderers/AllCellRenderers";
import {
    titleCase,
    campaignClassFormatter,
    formatCurrency,
    numericValueFormatter,
} from "@ts/utils";
import { customFilterOptions, mainContext } from "@ts/constants";
import { IMainContext, IRecommendationsData, SubModule, ParentModule } from "@ts/common-types";
import { useSelector } from "react-redux";
import { useGetModulesData } from "@hooks/AllCustomHooks";

const BudgetRecommendations = () => {
    const { setBudgetRecommendationsGridApi, budgetRecommendationsLoading } =
        useContext<IMainContext>(mainContext);
    const budgetRecommendationData = useSelector(
        (state: IRecommendationsData) => state.budgetRecommendationData.value
    );
    const gridParams = useGetModulesData({
        type: "grid-params",
        activeModuleName: SubModule.CAMPAIGN_BUDGET,
        activeParentModuleName: ParentModule.CAMPAIGN_RECOMMENDATIONS,
    });
    const budgetColumnDefs = useGetModulesData({
        type: "columns",
        colDefs: [
            {
                field: "campaign_name",
                headerName: "Campaign Name",
                minWidth: 300,
                maxWidth: 450,
                filter: "agTextColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["text"],
                },
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                tooltipField: "custom_search_term",
                valueFormatter: (params: ValueFormatterParams) => titleCase(params.value),
                // pinnedRowCellRenderer: blankCellRenderer,
                cellRenderer: function (params: ICellRendererParams) {
                    return titleCase(params.value || "");
                },
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    pinnedColumnValue: "Total",
                },
            },
            {
                field: "record_type",
                headerName: "Campaign Class",
                cellStyle: { whiteSpace: "normal" },
                autoHeight: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                valueFormatter: campaignClassFormatter,
                filterParams: {
                    showTooltips: true,
                    valueFormatter: campaignClassFormatter,
                    values: ["campaign"],
                },
            },
            {
                field: "budget",
                headerName: "Budget",
                filter: "agTextColumnFilter",
                maxWidth: 180,
                valueFormatter: (param: ValueFormatterParams) =>
                    !param.node.rowPinned
                        ? formatCurrency(param.value, param.data.country)
                        : "",
                cellRenderer: UpdateBidOrBudgetCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    module: "recommendation-campaign",
                },
            },
            {
                field: "percentSpentout",
                headerName: "% of spentout of budget",
                maxWidth: 170,
                minWidth: 120,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: false,
                    decimalPlace: 1,
                },
            },
            {
                headerName: "Impressions",
                field: "impression",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                headerName: "Clicks",
                field: "click",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                headerName: "Spends",
                field: "spend",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                sort: "desc",
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Orders",
                field: "orders",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
            },
            {
                headerName: "Sales",
                field: "sale",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CTR",
                cellStyle: { whiteSpace: "normal" },
                field: "ctr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
            },
            {
                headerName: "CPC",
                cellStyle: { whiteSpace: "normal" },
                field: "cpc",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
                hide: true,
            },
            {
                headerName: "CR",
                cellStyle: { whiteSpace: "normal" },
                field: "cr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
                hide: true,
            },
            {
                headerName: "CPA",
                cellStyle: { whiteSpace: "normal" },
                field: "cpa",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPercent: false,
                    hasPinnedColumnValue: true,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
                hide: true,
            },
            {
                headerName: "ACoS",
                cellStyle: { whiteSpace: "normal" },
                field: "acos",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
            },
            {
                headerName: "RoAS",
                cellStyle: { whiteSpace: "normal" },
                field: "roas",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
                hide: true,
            },
        ],
    });

    return (
        <div className="wrapper" style={{ flex: "1 1 0" }}>
            <div
                id="budget-grid"
                className="ag-theme-alpine"
                style={{ height: "100%", width: "100%" }}
            >
                <AgGridLayout
                    rowData={budgetRecommendationData}
                    columnDefs={budgetColumnDefs}
                    setGridApi={setBudgetRecommendationsGridApi}
                    loading={budgetRecommendationsLoading}
                    isTotalRowsReq={true}
                    rowHeight={gridParams?.rowHeight}
                />
            </div>
        </div>
    );
};

export default BudgetRecommendations;
